/**
 * Passkeys, browser side: enrol one, sign in with one, list and remove them.
 *
 * Authgate does the cryptography and keeps the credentials; this file only
 * moves the ceremony between the browser's WebAuthn API, which speaks
 * ArrayBuffers, and Authgate, which speaks base64url JSON. Calorio's passkeys
 * live under its own RP ID (calorio.dakir.app), so they are Calorio's alone:
 * the password manager does not offer them on any other app's sign-in.
 *
 * base64url, not base64: WebAuthn uses the URL-safe alphabet (`-_`, not `+/`)
 * and no padding. Feeding one to the other's decoder throws, from somewhere
 * deep in the browser that does not say which field was wrong.
 *
 * Every call is same-origin (the vhost and the dev proxy route /auth/* to
 * Authgate), so the session cookies travel and Authgate knows the application
 * from the vhost.
 */

export interface PasskeySummary {
  credentialId: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

function bytesToBase64Url(buffer: ArrayBuffer): string {
  const binary = String.fromCharCode(...new Uint8Array(buffer));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Whether this browser can do passkeys at all. */
export function passkeysSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator.credentials?.create === "function"
  );
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: "include",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    const detail = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(detail?.message || "That did not work. Please try again.");
  }
  return (await response.json()) as T;
}

/**
 * Whether Calorio has passkeys switched on (`scripts/apps.sh passkeys` in
 * auth-service). Asked before showing the button, so a sign-in page never
 * offers something that will fail. False on any error: the code still works.
 */
export async function passkeysEnabled(): Promise<boolean> {
  try {
    const response = await fetch("/auth/passkey/availability", { credentials: "include" });
    if (!response.ok) return false;
    return ((await response.json()) as { enabled?: boolean }).enabled === true;
  } catch {
    return false;
  }
}

/**
 * Enrols a passkey on this device. Needs a live session — Authgate enforces
 * it, because adding a way into an account without already being in it would
 * be a takeover — and Authgate emails the account that it happened.
 */
export async function registerPasskey(label: string): Promise<void> {
  const options = await call<{
    challenge: string;
    user: { id: string; name: string; displayName: string };
    excludeCredentials?: { id: string; type: "public-key" }[];
  } & Omit<PublicKeyCredentialCreationOptions, "challenge" | "user" | "excludeCredentials">>(
    "POST",
    "/auth/passkey/register/start",
  );

  const created = (await navigator.credentials.create({
    publicKey: {
      ...options,
      challenge: base64UrlToBytes(options.challenge),
      user: { ...options.user, id: base64UrlToBytes(options.user.id) },
      excludeCredentials: (options.excludeCredentials ?? []).map((c) => ({
        ...c,
        id: base64UrlToBytes(c.id),
      })),
    },
  })) as PublicKeyCredential | null;
  if (!created) throw new Error("No passkey was created.");

  const response = created.response as AuthenticatorAttestationResponse;
  await call("POST", "/auth/passkey/register/finish", {
    clientDataJSON: bytesToBase64Url(response.clientDataJSON),
    attestationObject: bytesToBase64Url(response.attestationObject),
    transports: response.getTransports?.() ?? [],
    label: label.trim() || "Passkey",
  });
}

/**
 * Signs in with a passkey and no email address. Usernameless: nothing is sent
 * before the ceremony, so this cannot be used to ask whether an account exists.
 * On success the session cookies are set.
 *
 * `signal` lets the page take the ceremony back: the browser's prompt stays up
 * until the person answers it or it times out, and a sign-in form that cannot
 * offer the other route meanwhile is stuck. Aborting rejects with an
 * `AbortError`, which is a choice, not a failure — callers show nothing for it.
 */
export async function signInWithPasskey(signal?: AbortSignal): Promise<void> {
  const options = await call<{
    challenge: string;
    allowCredentials?: { id: string; type: "public-key" }[];
  } & Omit<PublicKeyCredentialRequestOptions, "challenge" | "allowCredentials">>(
    "POST",
    "/auth/passkey/login/start",
  );
  signal?.throwIfAborted();

  const assertion = (await navigator.credentials.get({
    signal,
    publicKey: {
      ...options,
      challenge: base64UrlToBytes(options.challenge),
      allowCredentials: (options.allowCredentials ?? []).map((c) => ({
        ...c,
        id: base64UrlToBytes(c.id),
      })),
    },
  })) as PublicKeyCredential | null;
  if (!assertion) throw new Error("No passkey was used.");

  const response = assertion.response as AuthenticatorAssertionResponse;
  await call("POST", "/auth/passkey/login/finish", {
    credentialId: assertion.id,
    userHandle: response.userHandle ? bytesToBase64Url(response.userHandle) : null,
    authenticatorData: bytesToBase64Url(response.authenticatorData),
    clientDataJSON: bytesToBase64Url(response.clientDataJSON),
    signature: bytesToBase64Url(response.signature),
  });
}

/** This account's passkeys for Calorio. */
export function listPasskeys(): Promise<PasskeySummary[]> {
  return call<PasskeySummary[]>("GET", "/auth/passkeys");
}

/** Removes one. The emailed code always remains, so removing the last is safe. */
export async function removePasskey(credentialId: string): Promise<void> {
  await call("DELETE", `/auth/passkeys/${encodeURIComponent(credentialId)}`);
}

/**
 * A WebAuthn failure as something a person can act on. The browser reports
 * nearly everything — cancelled, timed out, no matching passkey — as a bare
 * NotAllowedError, whose own message helps nobody.
 */
export function describePasskeyError(error: unknown): string {
  const name = (error as { name?: string } | null)?.name;
  if (name === "NotAllowedError") {
    return "No passkey was used. You may have cancelled, or this device has no passkey for Calorio.";
  }
  if (name === "InvalidStateError") return "This device already has a passkey for Calorio.";
  if (name === "SecurityError") {
    return "This site isn't allowed to use passkeys — a configuration problem, not something you did.";
  }
  return (error as Error | null)?.message || "Something went wrong with the passkey.";
}
